import { type Product } from '@/features/shop/services/Product/types'

/**
 * Subset of Product used for variation logic.
 *
 * We assume all products in a variation set (variationProducts array) have the
 * same variation property dimensions (e.g., all have "Color" and "Size"), but with
 * different values (e.g., "Red"/"Large" vs "Blue"/"Small"). The property names/structure
 * is consistent across all variations - they should never have mismatched dimensions.
 */
export type ProductVariationSubset = Pick<Product, 'variationProperties'>

export const productByProperties = <T extends ProductVariationSubset>(
  products: T[],
  selectedProperties: Record<string, string>,
): { product: T; isPartial: boolean } | undefined => {
  const selectedCount = Object.keys(selectedProperties).length

  // matches first product where all selectedProperties are present,
  // even if they don't cover all properties on the product
  const product = products.find((p) =>
    p.variationProperties?.every(
      (prop) => !(prop.name in selectedProperties) || selectedProperties[prop.name] === prop.value,
    ),
  )

  if (!product) return undefined

  const totalProps = product.variationProperties?.length ?? 0
  const isPartial = selectedCount < totalProps

  return { product, isPartial }
}

type DimensionOption = {
  value: string
  swatchUrl?: string
  isAvailable: boolean
}

export const getDimensionValues = <T extends ProductVariationSubset>(
  variationProducts: T[],
  selectedProperties: Record<string, string>,
  priorityValues?: Record<string, string>,
) => {
  const uniqueDimensionValues = variationProducts.reduce<Record<string, DimensionOption[]>>(
    (acc, product) => {
      // create an object of the product's variation properties for easy access
      const propertiesRecord = Object.fromEntries(
        product.variationProperties?.map(({ name, value }) => [name, value]) ?? [],
      )

      // go through the variations for the product
      product.variationProperties?.forEach(({ name, value, swatchUrl }) => {
        // if variation has not been populated (first one), create the entry
        acc[name] ??= []

        // check if all other selected properties match this product
        const selectedPropertiesMatch = Object.entries(selectedProperties)
          .filter(([propName]) => propName !== name)
          .every(([propName, propValue]) => {
            return propertiesRecord[propName] === propValue
          })

        const existing = acc[name].find((option) => option.value === value)

        // if the value doesn't exist in the dimension array, add it
        if (!existing) {
          const option = {
            value,
            swatchUrl: swatchUrl ?? undefined,
            isAvailable: selectedPropertiesMatch,
          }
          if (priorityValues?.[name] === value) {
            // if a value is prioritized, add it to the front
            acc[name].unshift(option)
          } else {
            acc[name].push(option)
          }
        } else {
          // if value exists and availability is now true, update it
          existing.isAvailable ||= selectedPropertiesMatch
        }
      })

      return acc
    },
    {},
  )

  // only return swatches if all options have swatches
  return Object.entries(uniqueDimensionValues).map(([name, values]) => {
    const hasAllSwatches = values.every(({ swatchUrl }) => !!swatchUrl)
    return {
      name,
      hasAllSwatches,
      values,
    }
  })
}

// identifies properties that only have one option and returns them
export const getSingleOptionProperties = <T extends ProductVariationSubset>(
  variationProducts: T[],
): Record<string, string> => {
  const singleOptionProperties: Record<string, string | null> = {}

  for (const product of variationProducts) {
    // false for first product to ensure we don't early exit,
    // then true for every following product
    let allHaveMultiple = Object.keys(singleOptionProperties).length > 0

    product.variationProperties?.forEach(({ name, value }) => {
      if (!(name in singleOptionProperties)) {
        // first time seeing this property, set it to the value
        singleOptionProperties[name] = value
      } else if (singleOptionProperties[name] !== value) {
        // Mark as having multiple values
        singleOptionProperties[name] = null
      }

      // if ANY property has a single value, set to false so we can keep checking
      if (singleOptionProperties[name] !== null) {
        allHaveMultiple = false
      }
    })

    if (allHaveMultiple) {
      // if we evaluate a product that confirms all properties have more than one
      // option we should skip evaluation of further products and return an empty object
      return {}
    }
  }

  // only return the single option properties
  return Object.fromEntries(
    Object.entries(singleOptionProperties).filter(
      (entry): entry is [string, string] => entry[1] !== null,
    ),
  )
}
